import { TaskItem } from '../api';
import { statusMeta, STATUSES } from '../lib/status';
import { escapeHtml } from '../lib/utils';
import { ProjectContext } from './project';

function chartDefaults(): void {
  if (!Chart.defaults) return;
  Chart.defaults.font.family = "'Inter', sans-serif";
  Chart.defaults.color = '#6b7280';
}

export async function renderOverview(
  content: HTMLElement,
  ctx: ProjectContext
): Promise<void> {
  const { project, tasks } = ctx;

  const total = tasks.length;
  const completed = tasks.filter((t) => t.status === 'complete');
  const pointsDone = completed.reduce((s, t) => s + t.storyPoints, 0);
  const totalPoints = tasks.reduce((s, t) => s + t.storyPoints, 0);

  // Completed points per member
  const memberMap = new Map<string, number>();
  for (const t of completed) {
    const key = t.assigneeName || 'Unassigned';
    memberMap.set(key, (memberMap.get(key) || 0) + t.storyPoints);
  }
  const memberLabels = Array.from(memberMap.keys());
  const memberValues = Array.from(memberMap.values());

  // Status distribution
  const statusCounts = STATUSES.map((s) => tasks.filter((t) => t.status === s.key).length);

  content.innerHTML = `
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-value">${total}</div>
        <div class="stat-label">Total tasks</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${completed.length}</div>
        <div class="stat-label">Completed</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${pointsDone}<span style="font-size:16px;color:var(--text-3);"> / ${totalPoints}</span></div>
        <div class="stat-label">Story points done</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${project.members.length}</div>
        <div class="stat-label">Members</div>
      </div>
    </div>

    <div class="chart-grid">
      <div class="chart-card">
        <h3>Task status</h3>
        <div class="chart-box"><canvas id="overview-status-chart"></canvas></div>
      </div>
      <div class="chart-card">
        <h3>Completed points per member</h3>
        <div class="chart-box"><canvas id="overview-member-chart"></canvas></div>
      </div>
    </div>
  `;

  chartDefaults();

  const statusCanvas = content.querySelector('#overview-status-chart') as HTMLCanvasElement;
  if (statusCanvas) {
    new Chart(statusCanvas, {
      type: 'doughnut',
      data: {
        labels: STATUSES.map((s) => s.label),
        datasets: [
          {
            data: statusCounts,
            backgroundColor: STATUSES.map((s) => s.color),
            borderWidth: 2,
            borderColor: '#ffffff',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
        },
        cutout: '60%',
      },
    });
  }

  const memberCanvas = content.querySelector('#overview-member-chart') as HTMLCanvasElement;
  if (memberCanvas) {
    new Chart(memberCanvas, {
      type: 'bar',
      data: {
        labels: memberLabels.map((l) => escapeHtml(l)),
        datasets: [
          {
            label: 'Story points completed',
            data: memberValues,
            backgroundColor: '#4f46e5',
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }
}
